// ─── Route Service ──────────────────────────────────────────────────────
// Handles journey planning via HAFAS/Vendo /api/journeys endpoint.
// Maps HAFAS journey responses to our TrainRoute interface.

import { apiFetch } from "./api";
import { resolveHafasId } from "./hafasService";
import { registerCities, getCityById, requestCityBuild, enrichNewCities } from "./cityStore";
import { reportError } from "./errorService";
import { journeyInstant, stationDateTime } from "./journeyTime";
import { extractCityName, haversineKm } from "./geoUtils";
import type { City, TrainRoute, CityWithTimes } from "../data/types";

/** Derive a city_key slug from a cleaned city name (mirrors server-side slugify).
 *  Handles characters that don't decompose via Unicode NFD (ø, æ, ð, ł, đ, ß). */
function slugify(name: string): string {
    return name
        .toLowerCase()
        .replaceAll(/ø/g, "o").replaceAll(/æ/g, "ae").replaceAll(/ð/g, "d")
        .replaceAll(/ł/g, "l").replaceAll(/đ/g, "d").replaceAll(/ß/g, "ss")
        .normalize("NFD").replaceAll(/[\u0300-\u036f]/g, "")
        .replaceAll(/[^a-z0-9]+/g, "-")
        .replaceAll(/^-|-$/g, "");
}

// ─── HAFAS API Response Types ───────────────────────────────────────────

interface HafasStop {
    type: string;
    id?: string;
    name?: string;
    city?: string;
    location?: {
        type: string;
        id?: string;
        latitude: number;
        longitude: number;
    };
    // Runtime-augmented fields from augmentStop() on the server
    image_url?: string;
    description?: string;
    country?: string;
    population?: number;
}

interface HafasOperator {
    type: string;
    id: string;
    name: string;
}

interface HafasLine {
    type: string;
    id?: string;
    fahrtNr?: string;
    name?: string;
    productName?: string;
    mode?: string;
    product?: string;
    operator?: HafasOperator;
}

interface HafasRemark {
    type: string;
    code?: string;
    text?: string;
    summary?: string;
}

interface HafasStopover {
    cancelled?: boolean;
    stop: HafasStop;
    arrival?: string | null;
    plannedArrival?: string | null;
    departure?: string | null;
    plannedDeparture?: string | null;
}

interface HafasLeg {
    cancelled?: boolean;
    origin: HafasStop;
    destination: HafasStop;
    departure?: string | null;
    arrival?: string | null;
    plannedDeparture?: string | null;
    plannedArrival?: string | null;
    line?: HafasLine;
    tripId?: string;
    walking?: boolean;
    transfer?: boolean;
    stopovers?: HafasStopover[];
    direction?: string | null;
    remarks?: HafasRemark[];
}

interface HafasJourney {
    cancelled?: boolean;
    type: string;
    legs: HafasLeg[];
    price?: {
        amount: number;
        currency: string;
        hint?: string | null;
        partialFare?: boolean;
    } | null;
}

interface HafasJourneysResponse {
    journeys?: HafasJourney[];
    earlierRef?: string;
    laterRef?: string;
}

// ─── Sleeper Detection Constants ────────────────────────────────────────
// Remark attribute codes returned by db-vendo-client (verified Feb 2026).
// These are NOT the UTP standard codes (SN/WL/CC/BC) — those are different.
const SLEEPER_WAGON_CODES = new Set(["SW", "LW", "LI"]);

// ─── Journey Search ─────────────────────────────────────────────────────

/**
 * Search for real train routes between two stations.
 * Uses the HAFAS/Vendo /api/journeys endpoint via db-vendo-client.
 * Requires: Station objects. IDs will be resolved to HAFAS EVA numbers.
 */
export async function findRoutesLive(
    fromCity: City,
    toCity: City,
    date?: string,
    results: number = 5,
    signal?: AbortSignal,
): Promise<{ routes: TrainRoute[]; citiesMap: Map<string, City> }> {
    // Resolve station IDs to HAFAS EVA numbers (per CLAUDE.md rules)
    const [fromId, toId] = await Promise.all([
        resolveHafasId(fromCity, signal),
        resolveHafasId(toCity, signal),
    ]);

    const params: Record<string, string> = {
        from: fromId,
        to: toId,
        results: String(results),
        stopovers: "true",
        remarks: "true",
        tickets: "true",
        // Disable non-rail transit and commuter rail
        bus: "false",
        ferry: "false",
        subway: "false",
        tram: "false",
        taxi: "false",
        suburban: "false",
        language: "en",
    };

    if (date) {
        params.departure = journeyInstant(date, fromCity);
    }

    try {
        const response = await apiFetch<HafasJourneysResponse>(
            "/journeys",
            params,
            5 * 60 * 1000, // Cache 5 minutes
            signal
        );

        const citiesMap = new Map<string, City>();
        const routes: TrainRoute[] = [];

        if (!response.journeys) {
            return { routes: [], citiesMap: new Map() };
        }

        for (let i = 0; i < response.journeys.length; i++) {
            const journey = response.journeys[i];
            const result = hafasJourneyToTrainRoute(journey, i, citiesMap, fromCity, toCity);
            if (result) {
                routes.push(result);
            }
        }

        signal?.throwIfAborted();
        // Register all discovered stations so getStationById() can find them
        registerCities(citiesMap);

        // Client-side Wikipedia enrichment for cities missing image/description.
        // Fire-and-forget — results merge into the store as they arrive.
        enrichNewCities(citiesMap);

        return { routes, citiesMap };
    } catch (error) {
        if (signal?.aborted) throw error;
        reportError(error, {
            level: "critical",
            context: { from: fromCity.name, to: toCity.name },
        });
        throw error; // Let caller (useRouteSearch) handle UI
    }
}

// ─── Journey Mapping ────────────────────────────────────────────────────

function appendStationVisit(
    stationData: CityWithTimes[], place: City, arrivalIso?: string | null, departureIso?: string | null,
): void {
    const arrival = stationDateTime(arrivalIso, place).time;
    const departure = stationDateTime(departureIso, place).time;
    const previous = stationData.at(-1);
    // Adjacent train legs share one transfer visit: retain its incoming arrival
    // while adding the outgoing departure. Later returns remain separate visits.
    if (previous?.id === place.id && previous.arrivalInstant && !previous.departureInstant && !arrivalIso && departureIso) {
        previous.departure = departure;
        previous.departureInstant = departureIso;
        return;
    }
    const arrivalInstant = arrivalIso ?? undefined;
    const departureInstant = departureIso ?? undefined;
    const exists = stationData.some(s => s.id === place.id
        && s.arrivalInstant === arrivalInstant && s.departureInstant === departureInstant);
    if (!exists) stationData.push({ ...place, arrival, departure, arrivalInstant, departureInstant });
}

function cityForJourneyStop(stop: HafasStop, id: string): City {
    const cleanName = stop.city || extractCityName(stop.name || "");
    const slug = cleanName ? slugify(cleanName) : undefined;
    const candidate = getCityById(id) || (slug ? getCityById(slug) : undefined);
    const existing = candidate && (!stop.location || haversineKm(candidate.latitude, candidate.longitude,
        stop.location.latitude, stop.location.longitude) < 15) ? candidate : undefined;
    if (!existing && slug && cleanName) {
        requestCityBuild(slug, cleanName, stop.location?.latitude, stop.location?.longitude, id);
    }
    return {
        ...existing,
        id,
        name: stop.name || existing?.name || "Unknown",
        city: existing?.city || cleanName,
        country: existing?.country || stop.country || "",
        population: existing?.population || stop.population || 0,
        latitude: stop.location?.latitude ?? existing?.latitude ?? 0,
        longitude: stop.location?.longitude ?? existing?.longitude ?? 0,
        imageUrl: existing?.imageUrl || stop.image_url || null,
        description: existing?.description || stop.description || null,
    };
}

function collectJourneyStops(trainLegs: HafasLeg[], citiesMap: Map<string, City>) {
    const allStationIds: string[] = [];
    const stationData: CityWithTimes[] = [];

    // Helper to ensure a HAFAS stop is registered in stationsMap and stationData
    const processStop = (stop: HafasStop, arrivalIso?: string | null, departureIso?: string | null) => {
        const id = stop.id || `loc-${stop.location?.latitude}-${stop.location?.longitude}`;

        if (!allStationIds.includes(id)) allStationIds.push(id);
        if (!citiesMap.has(id)) citiesMap.set(id, cityForJourneyStop(stop, id));

        appendStationVisit(stationData, citiesMap.get(id)!, arrivalIso, departureIso);
        return id;
    };

    for (const leg of trainLegs) {
        // Origin of this leg
        processStop(leg.origin, null, leg.departure || leg.plannedDeparture);

        // Intermediate stopovers
        if (leg.stopovers) {
            for (const so of leg.stopovers) {
                // Skip if it's the origin or destination (already processed)
                if (so.cancelled) continue;
                if (so.stop.id === leg.origin.id || so.stop.id === leg.destination.id) continue;
                processStop(so.stop, so.arrival || so.plannedArrival, so.departure || so.plannedDeparture);
            }
        }

        // Destination of this leg
        processStop(leg.destination, leg.arrival || leg.plannedArrival, null);
    }

    return { allStationIds, stationData };
}

function hafasJourneyToTrainRoute(
    journey: HafasJourney,
    index: number,
    citiesMap: Map<string, City>,
    fromCity: City,
    toCity: City
): TrainRoute | null {
    if (journey.cancelled || journey.legs.some(leg => leg.cancelled)) return null;
    // Filter out walking/transfer legs — keep only actual train legs
    const trainLegs = journey.legs.filter(
        (leg) => !leg.walking && !leg.transfer && leg.line
    );

    if (trainLegs.length === 0) return null;


    const firstLeg = trainLegs[0];
    const lastLeg = trainLegs.at(-1)!;

    const { allStationIds, stationData } = collectJourneyStops(trainLegs, citiesMap);

    if (allStationIds.length < 2) return null;

    // ─── Duration ───────────────────────────────────────────────────
    const depTime = firstLeg.departure || firstLeg.plannedDeparture;
    const arrTime = lastLeg.arrival || lastLeg.plannedArrival;
    let durationMs = 0;
    if (depTime && arrTime) {
        durationMs = new Date(arrTime).getTime() - new Date(depTime).getTime();
    }

    if (!depTime || !arrTime || !Number.isFinite(durationMs) || durationMs <= 0) return null;

    // ─── Operators ──────────────────────────────────────────────────
    const operators = trainLegs
        .map((leg) => leg.line?.operator?.name || "")
        .filter(Boolean);
    const uniqueOperators = [...new Set(operators)];

    // ─── Sleeper Detection ──────────────────────────────────────────
    // Only authoritative signal: HAFAS/Vendo remark attribute codes.
    // SW = Sleeping-car, LW = Couchettes, LI = Sleeperette.
    const isSleeper = trainLegs.some((leg) =>
        leg.remarks?.some((r) => r.code && SLEEPER_WAGON_CODES.has(r.code))
    );

    // ─── Route Name ─────────────────────────────────────────────────
    const fromName = fromCity.city || firstLeg.origin.name || "";
    const toName = toCity.city || lastLeg.destination.name || "";
    const routeName =
        trainLegs.length === 1
            ? `${fromName} to ${toName}`
            : `${fromName} to ${toName} (${trainLegs.length} trains)`;

    // Use HAFAS tripId per leg for a stable, journey-unique identifier
    // instead of array index (which collides across different searches).
    const tripIdKey = trainLegs.map((l) => l.tripId).filter(Boolean).join("_");

    // Pre-resolve polyline coords from the already-built stationsMap.
    // Using stationsMap (not stationStore) ensures fresh HAFAS coords are used,
    // and we can filter zero-coords before they escape into the rest of the system.
    const routeCoords = allStationIds
        .map((id) => citiesMap.get(id))
        .filter((s): s is NonNullable<typeof s> => !!s && !!s.latitude && !!s.longitude)
        .map((s) => [s.latitude, s.longitude] as [number, number]);

    return {
        id: tripIdKey ? `hafas-${tripIdKey}` : `hafas-route-${fromCity.id}-${toCity.id}-${depTime}-${index}`,
        name: routeName,
        from: allStationIds[0],
        to: allStationIds.at(-1)!,
        stations: allStationIds,
        routeCoords: routeCoords.length >= 2 ? routeCoords : undefined,
        duration: formatDuration(durationMs),
        isSleeper,
        operator: uniqueOperators.join(" / ") || "Unknown",
        frequency: trainLegs.length > 1 ? `${trainLegs.length} connections` : "",
        departureTime: stationDateTime(depTime, firstLeg.origin.location ?? fromCity).time || "",
        arrivalTime: stationDateTime(arrTime, lastLeg.destination.location ?? toCity).time || "",
        departureDate: stationDateTime(depTime, firstLeg.origin.location ?? fromCity).date,
        arrivalDate: stationDateTime(arrTime, lastLeg.destination.location ?? toCity).date,
        departureInstant: depTime,
        arrivalInstant: arrTime,
        stationData,
        price: journey.price && !journey.price.partialFare && Number.isFinite(journey.price.amount) && journey.price.amount >= 0 ? { amount: journey.price.amount, currency: journey.price.currency } : null,
    };
}

// ─── Helpers ────────────────────────────────────────────────────────────

function formatDuration(ms: number): string {
    const totalMinutes = Math.round(ms / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours === 0) return `${minutes}m`;
    if (minutes === 0) return `${hours}h`;
    return `${hours}h ${minutes}m`;
}
